import { AddCircleOutline, Close, Refresh, Visibility } from "@mui/icons-material";
import { Box, Chip, IconButton, Tooltip, Typography } from "@mui/material";
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { closeConversation, getConversationModel, getConversations } from "../../../../../actions/messaging";
import { EstadosConversacion, EstadosConversacionTextos } from "../../../../../utils/messagingEnums";
import snackbarUtils from "../../../../../utils/snackbarUtils";
import ConfirmDialog from "../../../../commons/dialog/confirmDialog";
import { tableConfig } from "../../../../commons/table/config/tableConfig";
import { StyledAccident } from "../../../../styled/accident";
import { StyledFlex } from "../../../../styled/flex";
import ConversationDetailDialog from '../../../messaging/conversationDetailDialog';
import CardTitle from "../cardTitle";
import NewConversationModal from "./newConversationModal";

const getStatusColor = (estado) => {
  switch (estado) {
    case EstadosConversacionTextos[EstadosConversacion.ESPERANDO_RESPUESTA_PRESTADOR]:
      return { backgroundColor: "#ffe0b2", color: "#e65100" };
    case EstadosConversacionTextos[EstadosConversacion.ESPERANDO_RESPUESTA_ART]:
      return { backgroundColor: "#e3f2fd", color: "#0d47a1" };
    case EstadosConversacionTextos[EstadosConversacion.CERRADA]:
      return { backgroundColor: "#e0e0e0", color: "#616161" };
    default:
      return {};
  }
};

const AccidentMessaging = ({ conversations, isLoading }) => {
  const dispatch = useDispatch();
  const accident = useSelector((state) => state.accidentDashboard.accident) || useSelector((state) => state.accident.currentAccident);
  const [openNewModal, setOpenNewModal] = useState(false);
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [selectedSubtipo, setSelectedSubtipo] = useState(null);

  const [confirmClose, setConfirmClose] = useState({ open: false, idConversacion: null, idTipo: null, idSubtipo: null });

  const columns = useMemo(
    () => [
      {
        accessorKey: 'id',
        header: 'Conversación',
        Header: () => (
          <Box>
            <Typography variant="subtitle2">Conversación</Typography>
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
              Id Conversación Externa
            </Typography>
          </Box>
        ),
        Cell: ({ row }) => (
          <Typography variant="body2">
            {row.original.idConversacionExterna ? "#" + row.original.idConversacionExterna : "Pendiente Asignación"}
          </Typography>
        ),
      },
      {
        accessorKey: 'tipo',
        header: 'Tipo Mensaje',
      },
      {
        accessorKey: 'subtipo',
        header: 'Subtipo Mensaje',
      },
      {
        accessorKey: 'titulo',
        header: 'Título',
      },
      {
        accessorKey: 'estado',
        header: 'Estado',
        Cell: ({ row }) => (
          <Chip
            label={row.original.estado}
            size="small"
            sx={{ ...getStatusColor(row.original.estado), fontWeight: 500 }}
          />
        )
      },
      {
        accessorKey: 'ultimoMensaje',
        header: 'Último mensaje',
        Cell: ({ row }) => {
          const text = row.original.ultimoMensaje || "";
          const isTruncated = text.length > 75;
          const truncatedText = isTruncated ? text.substring(0, 75) + "..." : text;

          return (
            <Tooltip title={isTruncated ? text : ""} placement="top" arrow>
              <Box sx={{ maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {truncatedText}
              </Box>
            </Tooltip>
          );
        }
      },
      {
        accessorKey: 'fecha',
        header: 'Fecha',
      }
    ],
    []
  );

  const handleCloseConversation = () => {
    if (!confirmClose.idConversacion) return;
    dispatch(closeConversation(confirmClose.idConversacion, accident.mlCloudCoreArtId, confirmClose.idTipo, confirmClose.idSubtipo))
      .then(() => {
        snackbarUtils.success("Conversación cerrada correctamente.");
        dispatch(getConversations(accident.idSiniestro, accident.mlCloudCoreArtId));
      })
      .finally(() => {
        setConfirmClose({ open: false, idConversacion: null, idTipo: null, idSubtipo: null });
      });
  };

  const table = useMaterialReactTable({
    ...tableConfig,
    columns,
    data: conversations || [],
    initialState: { pagination: { pageSize: 5, pageIndex: 0 } },
    state: { isLoading: isLoading },
    enableRowActions: true,
    positionActionsColumn: 'last',
    enableTopToolbar: false,
    renderRowActions: ({ row }) => (
      <StyledAccident.TableActions>
        <Tooltip title="Ver mensajes">
          <IconButton
            color="primary"
            size="small"
            onClick={() => {
              setSelectedConversationId(row.original.idConversacion);
              setSelectedSubtipo(row.original.idSubtipo);
            }}
          >
            <Visibility />
          </IconButton>
        </Tooltip>
        {row.original.estado !== EstadosConversacionTextos[EstadosConversacion.CERRADA] && row.original.puedeCerrar && (
          <Tooltip title="Cerrar conversación">
            <IconButton
              color="primary"
              size="small"
              onClick={() => setConfirmClose({ open: true, idConversacion: row.original.idConversacion, idTipo: row.original.idTipo, idSubtipo: row.original.idSubtipo })}
            >
              <Close />
            </IconButton>
          </Tooltip>
        )}
        {row.original.estado === EstadosConversacionTextos[EstadosConversacion.CERRADA] && row.original.puedeReabrir && (
          <Tooltip title="Reabrir conversación">
            <IconButton
              color="primary"
              size="small"
              onClick={() => {
                setSelectedConversationId(row.original.idConversacion);
                setSelectedSubtipo(row.original.idSubtipo);
              }}
            >
              <Refresh />
            </IconButton>
          </Tooltip>
        )}
      </StyledAccident.TableActions>
    ),
    renderEmptyRowsFallback: () => (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        No hay conversaciones pendientes
      </Box>
    ),
  });

  const icons = [
    {
      Icon: AddCircleOutline,
      tooltip: "Nueva conversación",
      onClick: () => {
        dispatch(getConversationModel(accident.mlCloudCoreArtId));
        setOpenNewModal(true);
      },
    },
  ];

  const pendingCount = conversations?.reduce((acc, curr) => acc + (curr.mensajesPendientes || 0), 0) || 0;

  const cardTitleNode = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      MENSAJERÍA
      {pendingCount > 0 && (
        <Chip
          label={`${pendingCount} pendientes`}
          size="small"
          sx={{ backgroundColor: '#FFB020', color: '#fff', fontWeight: 'bold', height: 24, fontSize: '0.75rem' }}
        />
      )}
    </Box>
  );

  return (
    <StyledFlex.ColumnContainer>
      <CardTitle title={cardTitleNode} color="primary" icons={icons} />
      <Box sx={{ width: '100%', overflowX: 'auto' }}>
        <MaterialReactTable table={table} />
      </Box>
      <NewConversationModal
        open={openNewModal}
        handleClose={() => setOpenNewModal(false)}
        accident={accident}
      />
      <ConversationDetailDialog
        open={!!selectedConversationId}
        onClose={() => {
          setSelectedConversationId(null);
          setSelectedSubtipo(null);
        }}
        conversationId={selectedConversationId}
        idArt={accident.mlCloudCoreArtId}
        idSubtipo={selectedSubtipo}
        idSiniestro={accident.idSiniestro}
      />
      <ConfirmDialog
        open={confirmClose.open}
        title="Confirmar cierre"
        text="¿Está seguro que desea cerrar esta conversación?"
        handleClose={() => setConfirmClose({ open: false, idConversacion: null, idTipo: null, idSubtipo: null })}
        handleConfirm={handleCloseConversation}
      />
    </StyledFlex.ColumnContainer>
  );
};

export default AccidentMessaging;
